import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FeedComposer } from '@/components/feed/FeedComposer';
import { FeedList } from '@/components/feed/FeedList';
import { ReportModal } from '@/components/feed/ReportModal';
import { groupsAPI, feedAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Users, ChevronDown, Loader2, Lock } from 'lucide-react';

const CATEGORY_LABELS = {
  alcool: 'Álcool',
  nicotina: 'Nicotina',
  drogas: 'Drogas',
  cocaina: 'Cocaína',
  emocoes: 'Emoções',
  'sexo-afetividade': 'Sexo e Afetividade',
  'jogos-apostas': 'Jogos e Apostas',
  lgbt: 'LGBT+',
};

export function GroupPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingPostId, setReportingPostId] = useState(null);

  useEffect(() => {
    loadGroup();
  }, [slug]);

  useEffect(() => {
    if (group && group.is_member) {
      loadPosts();
    }
  }, [group]);

  const loadGroup = async () => {
    setLoading(true);
    try {
      const response = await groupsAPI.getGroup(slug);
      setGroup(response.data);
    } catch (error) {
      console.error('Failed to load group:', error);
      toast.error('Grupo não encontrado');
      navigate('/grupos');
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async (loadMore = false) => {
    if (!group || postsLoading) return;

    setPostsLoading(true);
    try {
      const response = await groupsAPI.getGroupFeed(group.id, loadMore ? cursor : null);
      const newPosts = response.data;

      if (newPosts.length === 0) {
        setHasMore(false);
      } else {
        setPosts((prev) => (loadMore ? [...prev, ...newPosts] : newPosts));
        const lastPost = newPosts[newPosts.length - 1];
        setCursor(lastPost.created_at);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
      toast.error('Erro ao carregar posts');
    } finally {
      setPostsLoading(false);
    }
  };

  const handleJoin = async () => {
    setIsJoining(true);
    try {
      await groupsAPI.joinGroup(group.id);
      toast.success(`Você entrou em ${group.name}!`);
      
      // Reload group to update membership status
      await loadGroup();
    } catch (error) {
      console.error('Failed to join group:', error);
      toast.error(error.response?.data?.detail || 'Erro ao entrar no grupo');
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveConfirm = async () => {
    setIsLeaving(true);
    try {
      await groupsAPI.leaveGroup(group.id);
      toast.success(`Você saiu de ${group.name}`);
      setShowLeaveDialog(false);
      
      // Redirect to groups page
      navigate('/grupos');
    } catch (error) {
      console.error('Failed to leave group:', error);
      toast.error(error.response?.data?.detail || 'Erro ao sair do grupo');
    } finally {
      setIsLeaving(false);
    }
  };

  const handleCreatePost = async (postData) => {
    const response = await groupsAPI.createGroupPost(group.id, postData);
    const newPost = response.data;
    setPosts((prev) => [newPost, ...prev]);
  };

  const handleLike = async (postId, shouldLike) => {
    if (shouldLike) {
      await feedAPI.likePost(postId);
    } else {
      await feedAPI.unlikePost(postId);
    }
  };

  const handleComment = async (postId) => {
    console.log('Comment on post:', postId);
  };

  const handleReportOpen = (postId) => {
    setReportingPostId(postId);
    setReportModalOpen(true);
  };

  const handleReportSubmit = async (postId, reason) => {
    await feedAPI.reportPost(postId, reason);
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="group-page">
      {/* Header */}
      <Card className="mb-6">
        {/* Cover */}
        {group.cover_url ? (
          <div className="h-48 w-full overflow-hidden rounded-t-lg">
            <img
              src={group.cover_url}
              alt={`Cover de ${group.name}`}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="h-48 w-full bg-gradient-to-br from-blue-100 to-indigo-200 rounded-t-lg" />
        )}

        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <Avatar className="h-20 w-20 border-4 border-background -mt-16 relative z-10">
              <AvatarImage src={group.cover_url} alt={group.name} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-2xl">
                {getInitials(group.name)}
              </AvatarFallback>
            </Avatar>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold mb-1" data-testid="group-page-name">
                    {group.name}
                  </h1>
                  <Badge variant="secondary" data-testid="group-page-category">
                    {CATEGORY_LABELS[group.category] || group.category}
                  </Badge>
                </div>

                {/* Join/Leave Button */}
                {group.is_member ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowLeaveDialog(true)}
                    disabled={isLeaving}
                    data-testid="leave-group-button"
                    aria-label={`Sair do grupo ${group.name}`}
                  >
                    {isLeaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saindo...
                      </>
                    ) : (
                      'Sair do Grupo'
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleJoin}
                    disabled={isJoining}
                    data-testid="join-group-button"
                    aria-label={`Entrar no grupo ${group.name}`}
                  >
                    {isJoining ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Entrando...
                      </>
                    ) : (
                      'Entrar no Grupo'
                    )}
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-1 mt-3 text-muted-foreground">
                <Users className="h-4 w-4" />
                <span data-testid="group-page-member-count">
                  {group.member_count} {group.member_count === 1 ? 'membro' : 'membros'}
                </span>
              </div>

              <p className="text-muted-foreground mt-3" data-testid="group-page-description">
                {group.description}
              </p>
            </div>
          </div>

          {/* Rules */}
          {group.rules && (
            <div className="mt-4">
              <Collapsible open={rulesOpen} onOpenChange={setRulesOpen}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full justify-between"
                    data-testid="rules-toggle"
                    aria-expanded={rulesOpen}
                    aria-label="Ver regras do grupo"
                  >
                    <span className="font-semibold">Regras do Grupo</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${rulesOpen ? 'rotate-180' : ''}`}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <Card className="p-4 mt-2 bg-muted/50">
                    <p className="text-sm whitespace-pre-wrap" data-testid="group-rules">
                      {group.rules}
                    </p>
                  </Card>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </div>
      </Card>

      {/* Content */}
      {group.is_member ? (
        <>
          <FeedComposer onPostCreated={handleCreatePost} groupId={group.id} />

          <div className="mb-4">
            <h2 className="text-xl font-semibold">Posts do Grupo</h2>
          </div>

          <FeedList
            posts={posts}
            loading={postsLoading}
            error={null}
            hasMore={hasMore}
            onLoadMore={() => loadPosts(true)}
            onLike={handleLike}
            onComment={handleComment}
            onReport={handleReportOpen}
          />
        </>
      ) : (
        <Card className="p-12 text-center">
          <Lock className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Conteúdo Restrito</h3>
          <p className="text-muted-foreground mb-4">
            Entre no grupo para ver e participar das discussões
          </p>
          <Button onClick={handleJoin} disabled={isJoining} data-testid="join-group-cta">
            {isJoining ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Entrando...
              </>
            ) : (
              'Entrar no Grupo'
            )}
          </Button>
        </Card>
      )}

      {/* Leave Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent data-testid="leave-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sair do grupo?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem certeza que deseja sair de {group.name}? Você poderá entrar novamente a
              qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLeaving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeaveConfirm}
              disabled={isLeaving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-leave-button"
            >
              {isLeaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saindo...
                </>
              ) : (
                'Sair'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSubmit={handleReportSubmit}
        postId={reportingPostId}
      />
    </div>
  );
}
